// #1150: route 単位のコード分割ヘルパ。
//
// React.lazy そのままではなく wrapper を挟むのは、デプロイ跨ぎの chunk 消失に
// 対応するため: 旧 index.html を開いたままのクライアントが、デプロイで消えた
// 旧ハッシュの chunk を要求すると dynamic import が reject する
// ("Failed to fetch dynamically imported module")。この場合は一度だけ全体を
// リロードして新しい index.html / chunk 一式を取り直す。恒久的な障害 (オフライン
// 等) でリロードが無限ループしないよう、sessionStorage で 1 回に制限し、
// ロード成功時にフラグを戻す。
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RELOAD_FLAG = 'lazyPage:reloaded'

// private mode 等で storage アクセス自体が throw する環境があるため握りつぶす
// (その環境ではリロードガードなしで即 throw = ErrorBoundary に落ちるだけ)
function getReloadFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1'
  } catch {
    return true
  }
}

function setReloadFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, '1')
    else sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    /* noop */
  }
}

export function lazyPage<TProps extends object>(
  load: () => Promise<{ default: ComponentType<TProps> }>
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(async () => {
    try {
      const mod = await load()
      setReloadFlag(false)
      return mod
    } catch (err) {
      if (!getReloadFlag()) {
        setReloadFlag(true)
        window.location.reload()
        // リロードが走るまで pending のままにして ErrorBoundary 落ちを防ぐ
        return new Promise<never>(() => {})
      }
      throw err
    }
  })
}
